import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { errMsg } from "@/lib/api";

export default function AdminPricing() {
  const [plans, setPlans] = useState([]);
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
    api.get("/admin/config").then(({ data }) => setConfig(data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const savePlan = async (plan) => {
    try {
      await api.put(`/admin/plans/${plan.id}`, {
        name: plan.name, price_monthly: Number(plan.price_monthly), price_yearly: Number(plan.price_yearly),
        max_active_jobs: Number(plan.max_active_jobs), custom: !!plan.custom,
        features: plan.features || [], highlight: !!plan.highlight,
      });
      toast.success(`${plan.name} updated`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveConfig = async () => {
    setBusy(true);
    try {
      await api.put("/admin/config", {
        gst_pct: Number(config.gst_pct),
        recruitment: {
          ...config.recruitment,
          prepaid_amount: Number(config.recruitment.prepaid_amount),
          postpaid_pct: Number(config.recruitment.postpaid_pct),
        },
        course_commission_pct: Number(config.course_commission_pct),
        franchise_fee: Number(config.franchise_fee),
        partner_payout: {
          prepaid_amount: Number(config.partner_payout.prepaid_amount),
          postpaid_pct: Number(config.partner_payout.postpaid_pct),
          franchise_boost_pct: Number(config.partner_payout.franchise_boost_pct || 0),
          tiers: (config.partner_payout.tiers || []).map((t) => ({
            label: t.label, min_placements: Number(t.min_placements),
            prepaid_amount: Number(t.prepaid_amount), postpaid_pct: Number(t.postpaid_pct),
          })),
        },
        wallet_packages: config.wallet_packages.map((p) => ({ ...p, price: Number(p.price), credits: Number(p.credits) })),
        talent_plans: (config.talent_plans || []).map((p) => ({ ...p, price: Number(p.price), views: Number(p.views) })),
        payroll_by_country: Object.fromEntries(
          Object.entries(config.payroll_by_country || {}).map(([k, v]) => [k, {
            ...v, basic_pct: Number(v.basic_pct), hra_pct: Number(v.hra_pct),
            deductions: Object.fromEntries(Object.entries(v.deductions || {}).map(([dk, dv]) => [dk, Number(dv)])),
          }])
        ),
      });
      toast.success("Platform configuration saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const updatePlan = (id, key, value) =>
    setPlans((ps) => ps.map((p) => (p.id === id ? { ...p, [key]: value } : p)));

  if (!config) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div data-testid="admin-pricing-page">
      <Tabs defaultValue="plans">
        <TabsList className="mb-6">
          <TabsTrigger value="plans" data-testid="tab-plans">Subscription Plans</TabsTrigger>
          <TabsTrigger value="commercials" data-testid="tab-commercials">Recruitment & Commercials</TabsTrigger>
          <TabsTrigger value="wallet" data-testid="tab-wallet">Wallet Packages</TabsTrigger>
          <TabsTrigger value="talent" data-testid="tab-talent">Talent Search Plans</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll-config">Payroll by Country</TabsTrigger>
        </TabsList>

        <TabsContent value="plans">
          <div className="grid lg:grid-cols-2 gap-6">
            {plans.map((p) => (
              <div key={p.id} className="bg-white border border-border/60 rounded-xl p-6" data-testid={`edit-plan-${p.id}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-semibold text-[#0B1B3D]">{p.name}</h3>
                  {p.custom && <span className="text-xs bg-slate-100 rounded-full px-3 py-1">Custom pricing</span>}
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Monthly (₹)</Label>
                    <Input type="number" value={p.price_monthly} disabled={p.custom} onChange={(e) => updatePlan(p.id, "price_monthly", e.target.value)} className="mt-1" data-testid={`plan-monthly-${p.id}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Yearly (₹)</Label>
                    <Input type="number" value={p.price_yearly} disabled={p.custom} onChange={(e) => updatePlan(p.id, "price_yearly", e.target.value)} className="mt-1" data-testid={`plan-yearly-${p.id}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Max active jobs</Label>
                    <Input type="number" value={p.max_active_jobs} onChange={(e) => updatePlan(p.id, "max_active_jobs", e.target.value)} className="mt-1" data-testid={`plan-jobs-${p.id}`} />
                  </div>
                </div>
                <Button size="sm" onClick={() => savePlan(p)} className="mt-5 rounded-full bg-[#0B1B3D]" data-testid={`save-plan-${p.id}`}>
                  Save {p.name}
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="commercials">
          <div className="bg-white border border-border/60 rounded-xl p-6 max-w-2xl space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label>Prepaid fee / candidate (₹)</Label>
                <Input type="number" value={config.recruitment.prepaid_amount} onChange={(e) => setConfig({ ...config, recruitment: { ...config.recruitment, prepaid_amount: e.target.value } })} className="mt-1.5" data-testid="cfg-prepaid" />
              </div>
              <div>
                <Label>Postpaid % of Annual CTC</Label>
                <Input type="number" step="0.01" value={config.recruitment.postpaid_pct} onChange={(e) => setConfig({ ...config, recruitment: { ...config.recruitment, postpaid_pct: e.target.value } })} className="mt-1.5" data-testid="cfg-postpaid" />
              </div>
              <div>
                <Label>GST %</Label>
                <Input type="number" value={config.gst_pct} onChange={(e) => setConfig({ ...config, gst_pct: e.target.value })} className="mt-1.5" data-testid="cfg-gst" />
              </div>
              <div>
                <Label>Course commission %</Label>
                <Input type="number" value={config.course_commission_pct} onChange={(e) => setConfig({ ...config, course_commission_pct: e.target.value })} className="mt-1.5" data-testid="cfg-commission" />
              </div>
              <div>
                <Label>Partner payout — prepaid (₹)</Label>
                <Input type="number" value={config.partner_payout.prepaid_amount} onChange={(e) => setConfig({ ...config, partner_payout: { ...config.partner_payout, prepaid_amount: e.target.value } })} className="mt-1.5" data-testid="cfg-payout-prepaid" />
              </div>
              <div>
                <Label>Partner payout — postpaid % of CTC</Label>
                <Input type="number" step="0.01" value={config.partner_payout.postpaid_pct} onChange={(e) => setConfig({ ...config, partner_payout: { ...config.partner_payout, postpaid_pct: e.target.value } })} className="mt-1.5" data-testid="cfg-payout-postpaid" />
              </div>
              <div>
                <Label>Franchise package fee (₹)</Label>
                <Input type="number" value={config.franchise_fee} onChange={(e) => setConfig({ ...config, franchise_fee: e.target.value })} className="mt-1.5" data-testid="cfg-franchise" />
              </div>
              <div>
                <Label>Franchise payout boost %</Label>
                <Input type="number" step="0.01" value={config.partner_payout.franchise_boost_pct ?? 0} onChange={(e) => setConfig({ ...config, partner_payout: { ...config.partner_payout, franchise_boost_pct: e.target.value } })} className="mt-1.5" data-testid="cfg-franchise-boost" />
              </div>
            </div>
            <div data-testid="payout-tiers-editor">
              <div className="flex items-center justify-between mb-3">
                <Label>Partner payout tiers (by lifetime placements)</Label>
                <Button
                  size="sm" variant="outline" className="rounded-full"
                  onClick={() => setConfig({ ...config, partner_payout: { ...config.partner_payout, tiers: [...(config.partner_payout.tiers || []), { label: "", min_placements: 0, prepaid_amount: 0, postpaid_pct: 0 }] } })}
                  data-testid="add-tier-btn"
                >
                  Add Tier
                </Button>
              </div>
              <div className="space-y-3">
                {(config.partner_payout.tiers || []).map((t, idx) => {
                  const setTier = (key, value) => setConfig({
                    ...config,
                    partner_payout: { ...config.partner_payout, tiers: config.partner_payout.tiers.map((x, i) => i === idx ? { ...x, [key]: value } : x) },
                  });
                  return (
                    <div key={idx} className="grid grid-cols-5 gap-3 items-end bg-[#F8FAFC] border border-border/60 rounded-lg p-3" data-testid={`tier-row-${idx}`}>
                      <div><Label className="text-xs">Label</Label><Input value={t.label} onChange={(e) => setTier("label", e.target.value)} className="mt-1" data-testid={`tier-label-${idx}`} /></div>
                      <div><Label className="text-xs">Min placements</Label><Input type="number" value={t.min_placements} onChange={(e) => setTier("min_placements", e.target.value)} className="mt-1" data-testid={`tier-min-${idx}`} /></div>
                      <div><Label className="text-xs">Prepaid (₹)</Label><Input type="number" value={t.prepaid_amount} onChange={(e) => setTier("prepaid_amount", e.target.value)} className="mt-1" data-testid={`tier-prepaid-${idx}`} /></div>
                      <div><Label className="text-xs">Postpaid %</Label><Input type="number" step="0.01" value={t.postpaid_pct} onChange={(e) => setTier("postpaid_pct", e.target.value)} className="mt-1" data-testid={`tier-postpaid-${idx}`} /></div>
                      <Button
                        size="sm" variant="ghost" className="text-red-600"
                        onClick={() => setConfig({ ...config, partner_payout: { ...config.partner_payout, tiers: config.partner_payout.tiers.filter((_, i) => i !== idx) } })}
                        data-testid={`remove-tier-${idx}`}
                      >
                        Remove
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
            <Button onClick={saveConfig} disabled={busy} className="rounded-full bg-[#0B1B3D]" data-testid="save-config-btn">
              {busy ? "Saving…" : "Save Configuration"}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="wallet">
          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl">
            {config.wallet_packages.map((p, idx) => (
              <div key={p.id} className="bg-white border border-border/60 rounded-xl p-6" data-testid={`cfg-wallet-${p.id}`}>
                <Label className="text-xs">Credits</Label>
                <Input
                  type="number"
                  value={p.credits}
                  onChange={(e) => setConfig({ ...config, wallet_packages: config.wallet_packages.map((x, i) => i === idx ? { ...x, credits: e.target.value } : x) })}
                  className="mt-1 mb-3"
                />
                <Label className="text-xs">Price (₹)</Label>
                <Input
                  type="number"
                  value={p.price}
                  onChange={(e) => setConfig({ ...config, wallet_packages: config.wallet_packages.map((x, i) => i === idx ? { ...x, price: e.target.value } : x) })}
                  className="mt-1"
                />
              </div>
            ))}
          </div>
          <Button onClick={saveConfig} disabled={busy} className="mt-6 rounded-full bg-[#0B1B3D]" data-testid="save-wallet-btn">
            Save Wallet Packages
          </Button>
        </TabsContent>

        <TabsContent value="talent">
          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl">
            {(config.talent_plans || []).map((p, idx) => (
              <div key={p.id} className="bg-white border border-border/60 rounded-xl p-6" data-testid={`cfg-talent-${p.id}`}>
                <p className="font-heading font-semibold text-sm mb-3">{p.name}</p>
                <Label className="text-xs">Profile views / month</Label>
                <Input
                  type="number"
                  value={p.views}
                  onChange={(e) => setConfig({ ...config, talent_plans: config.talent_plans.map((x, i) => i === idx ? { ...x, views: e.target.value } : x) })}
                  className="mt-1 mb-3"
                />
                <Label className="text-xs">Price (₹)</Label>
                <Input
                  type="number"
                  value={p.price}
                  onChange={(e) => setConfig({ ...config, talent_plans: config.talent_plans.map((x, i) => i === idx ? { ...x, price: e.target.value } : x) })}
                  className="mt-1"
                />
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Bundled into these subscription plans: {(config.talent_bundled_plans || []).join(", ")} (edit in config if needed).
          </p>
          <Button onClick={saveConfig} disabled={busy} className="mt-6 rounded-full bg-[#0B1B3D]" data-testid="save-talent-btn">
            Save Talent Plans
          </Button>
        </TabsContent>

        <TabsContent value="payroll">
          <div className="grid sm:grid-cols-2 gap-6 max-w-4xl">
            {Object.entries(config.payroll_by_country || {}).map(([code, m]) => (
              <div key={code} className="bg-white border border-border/60 rounded-xl p-6" data-testid={`cfg-payroll-${code}`}>
                <p className="font-heading font-semibold text-sm mb-3">{m.label || code} payroll module</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Basic %</Label>
                    <Input type="number" value={m.basic_pct} className="mt-1"
                      onChange={(e) => setConfig({ ...config, payroll_by_country: { ...config.payroll_by_country, [code]: { ...m, basic_pct: e.target.value } } })} />
                  </div>
                  <div>
                    <Label className="text-xs">HRA %</Label>
                    <Input type="number" value={m.hra_pct} className="mt-1"
                      onChange={(e) => setConfig({ ...config, payroll_by_country: { ...config.payroll_by_country, [code]: { ...m, hra_pct: e.target.value } } })} />
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  {Object.entries(m.deductions || {}).map(([dk, dv]) => (
                    <div key={dk} className="flex items-center gap-3">
                      <Label className="text-xs w-40">{({ pf_pct: "PF %", tds_pct: "TDS %", professional_tax: "Professional Tax (₹)" })[dk] || dk.replace(/_/g, " ")}</Label>
                      <Input type="number" value={dv} className="h-8"
                        onChange={(e) => setConfig({ ...config, payroll_by_country: { ...config.payroll_by_country, [code]: { ...m, deductions: { ...m.deductions, [dk]: e.target.value } } } })} />
                    </div>
                  ))}
                  {Object.keys(m.deductions || {}).length === 0 && (
                    <p className="text-xs text-muted-foreground">No statutory deductions configured for this country.</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <Button onClick={saveConfig} disabled={busy} className="mt-6 rounded-full bg-[#0B1B3D]" data-testid="save-payroll-btn">
            Save Payroll Modules
          </Button>
        </TabsContent>
      </Tabs>
    </div>
  );
}
